import React, { useContext, useState, useEffect, useRef, FC } from 'react'

import { Button, Form, Grid, Header, Modal, Rating } from 'semantic-ui-react'

import Data, { IClip, ProjectQuestionResponse } from '../../api/Data'
import { maxLength } from '../../config/constants'
import { S3_BUCKET_URL } from '../../config/environments'
import { GlobalContext } from '../../contexts/GlobalContext'

import CustomLabel from '../../components/common/custom-label/CustomLabel'
import UploadLocationField from '../../components/modals/LocationSearchField'
import MultiSelectQuestion from './MultiSelectQuestion'
import SingleSelectQuestion from './SingleSelectQuestion'
import FreeTextQuestion from './FreeTextQuestion'
import './UploadForm.scss'
import type { AdminUploadDraft } from './UploadFromComputerModal'

// Reducer helper
const reducer = (arrayToUpdate: IClip[], indexToUpdate: number, newFields: any) =>
  arrayToUpdate.reduce((newUploads: IClip[], upload, index) => {
    if (index === Number(indexToUpdate)) {
      newUploads.push({ ...upload, ...newFields })
    } else newUploads.push(upload)

    return newUploads
  }, [])

const UploadForm: FC<IProps> = ({
  clipIds,
  displaySetting,
  projectQuestions,
  uploads,
  setUploads,
  setShowModal,
  adminUploadDraft,
}) => {
  const { isMobile } = useContext(GlobalContext)

  // States
  const [placeSuggestionsArr, setPlaceSuggestionsArr] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string[] | null>(null)

  // References
  const didFetchPlaces = useRef(false)

  const toQuestionKeyFormat = (question: ProjectQuestionResponse, clipIndex: number) =>
    `question_${question.id}_${clipIndex}`

  // Fetch clips from the database
  useEffect(() => {
    if (adminUploadDraft) {
      const directUploads = adminUploadDraft.session.files.map((sourceAsset, index) => {
        const upload: any = {
          id: -(index + 1),
          sourceAssetId: sourceAsset.id,
          contributor: adminUploadDraft.contributorNames[index],
          previewUrl: URL.createObjectURL(adminUploadDraft.files[index]),
          rating: 0,
          tags: [],
          place: '',
          location: '',
          lat1: null,
          lon1: null,
        }
        projectQuestions?.forEach((question) => {
          upload[toQuestionKeyFormat(question, index)] = question.type === 'MultiSelect' ? [] : ''
        })
        return upload
      })
      setUploads(directUploads)
      return () => directUploads.forEach((upload) => URL.revokeObjectURL(upload.previewUrl))
    }

    const fetchUploads = async () => {
      try {
        const uploadPromises = clipIds.map((clipId) => Data.getClip(clipId))

        const results = await Promise.all(uploadPromises)

        if (results.some((result) => result.error)) return alert('An error occurred when loading clips from db')
        if (results.some((result) => result.data === null)) return alert('An error occurred when loading clips from db')

        const newUploads: IClip[] = results.reduce(
          (acc: IClip[], result) => (result.data ? [...acc, result.data] : acc),
          []
        )

        const uploadsWithQuestions = newUploads.map((upload, index) => {
          const dynamicUpload = { ...upload } as IClipWithQuestions

          if (projectQuestions && projectQuestions.length > 0) {
            projectQuestions.forEach((question) => {
              const questionKey = toQuestionKeyFormat(question, index)
              dynamicUpload[questionKey] = question.type === 'MultiSelect' ? [] : ''
            })
          }

          return dynamicUpload
        })

        setUploads(uploadsWithQuestions)
      } catch (err) {
        console.error('Something wrong happened', err)
        alert('Something wrong happened')
      }
    }

    fetchUploads()
  }, [adminUploadDraft, clipIds, setUploads, projectQuestions])

  useEffect(() => {
    const fetchPlaceSuggestions = async () => {
      const promises = uploads.map((upload) => {
        return Data.getClipPlaceSuggestions(upload.id)
      })

      const promisesArr = (await Promise.all(promises)).map((promise) => promise?.data || null)

      return setPlaceSuggestionsArr([...promisesArr])
    }
    // Here we are using a trick to avoid this effect
    // to trigger each time "uploads" is updated
    if (adminUploadDraft || didFetchPlaces.current || uploads.length === 0) return
    fetchPlaceSuggestions()
    didFetchPlaces.current = true
  }, [adminUploadDraft, uploads, uploads.length])

  // Update state once the user picks up a place and location
  const setLocation = (
    indexToUpdate: number,
    suggestion: { place: string; location: string; lat: number; lng: number }
  ) => {
    const { place, location, lat, lng } = suggestion

    const fieldsToUpdate = { place, location, lat1: lat, lon1: lng }

    const newUploadArray = reducer(uploads, indexToUpdate, fieldsToUpdate)

    setUploads(newUploadArray)
  }

  // Reset location
  const resetLocation = (indexToUpdate: number) => {
    const fieldsToUpdate = { place: '', location: '', lat1: '', lon1: '' }
    const newUploadArray = reducer(uploads, indexToUpdate, fieldsToUpdate)

    setUploads(newUploadArray)
  }

  const handleChange = (name: string, value: any, indexToUpdate: number) => {
    setErrorMessage(null)
    const newUploadArray = reducer(uploads, indexToUpdate, { [name]: value })
    setUploads(newUploadArray)
  }

  const validateForm = () => {
    const { showRating } = displaySetting
    const formErrors = (errorMessage || []).filter((e) => e.startsWith('validation-error_')) // keep validation errors

    uploads.forEach((upload, index) => {
      const uploadWithQuestions = upload as IClipWithQuestions

      const { contributor, rating } = uploadWithQuestions

      if (!contributor) formErrors.push(`${index}-name`)
      if (showRating && ![1, 2, 3, 4, 5].includes(rating as number)) formErrors.push(`${index}-rating`)

      if (projectQuestions) {
        projectQuestions.forEach((question) => {
          const questionKey = toQuestionKeyFormat(question, index)
          const questionValue = uploadWithQuestions[questionKey]

          if (
            questionValue === undefined ||
            questionValue === null ||
            questionValue === '' ||
            (Array.isArray(questionValue) && questionValue.length === 0)
          ) {
            formErrors.push(`${index}-${questionKey}`)
          }
        })
      }
    })

    if (formErrors.length > 0) {
      setErrorMessage(formErrors)
      return false
    }

    setErrorMessage(null)
    return completeUpload()
  }

  const addValidationError = (message: string, question: ProjectQuestionResponse, clipIndex: number) => {
    setErrorMessage([
      ...(errorMessage || []),
      `validation-error_${toQuestionKeyFormat(question, clipIndex)}_${message}`,
    ])
  }
  const removeValidationError = (question: ProjectQuestionResponse, clipIndex: number) => {
    const finalErrorsArray = (errorMessage || []).filter(
      (e) => !e.startsWith(`validation-error_${toQuestionKeyFormat(question, clipIndex)}_`)
    )
    setErrorMessage(finalErrorsArray.length ? finalErrorsArray : null)
  }

  // Update clip with form metadata (caption, place, project tag...)
  const completeUpload = async () => {
    setIsLoading(true)

    const uploadPayloads = uploads.map((upload, index) => {
      const uploadWithQuestions = upload as IClipWithQuestions

      const { contributor, place, location, lat1, lon1, rating, tags } = uploadWithQuestions

      const updateData = {
        id: uploadWithQuestions.id,
        contributor,
        place,
        location,
        lat1,
        lon1,
        rating,
        tags,
        clipAnswers: [] as ClipAnswer[],
      }

      if (projectQuestions) {
        projectQuestions.forEach((projectQuestion) => {
          const questionKey = toQuestionKeyFormat(projectQuestion, index)
          const questionValue = uploadWithQuestions[questionKey]

          if (projectQuestion.datasetId) {
            const datasetRowNumbers: number[] = []

            if (projectQuestion.type === 'MultiSelect' && Array.isArray(questionValue)) {
              questionValue.forEach((val) => {
                const matchingRow = projectQuestion.datasetKeys?.find((key) => key.value === val)
                if (matchingRow) {
                  datasetRowNumbers.push(matchingRow.rowNumber)
                }
              })
            } else if (projectQuestion.type === 'SingleSelect') {
              const matchingRow = projectQuestion.datasetKeys?.find((key) => key.value === questionValue)
              if (matchingRow) {
                datasetRowNumbers.push(matchingRow.rowNumber)
              }
            } else if (projectQuestion.type === 'FreeText') {
              const matchedDatasetRowNumbers: number[] = uploadWithQuestions[`${questionKey}_dataset-row-numbers`]
              datasetRowNumbers.push(...matchedDatasetRowNumbers)
            }

            updateData.clipAnswers.push({
              questionId: projectQuestion.id,
              datasetRowNumbers: datasetRowNumbers,
            })
          } else {
            if (projectQuestion.type === 'MultiSelect' && Array.isArray(questionValue)) {
              updateData.clipAnswers.push({
                questionId: projectQuestion.id,
                value: questionValue.join('|'), // Join array values for MultiSelect
              })
            } else {
              updateData.clipAnswers.push({
                questionId: projectQuestion.id,
                value: questionValue || '',
              })
            }
          }
        })
      }

      return adminUploadDraft
        ? { ...updateData, sourceAssetId: uploadWithQuestions.sourceAssetId, id: undefined }
        : updateData
    })

    try {
      if (adminUploadDraft) {
        const response = await Data.finalizeAdminUploadSession(
          adminUploadDraft.session.id,
          adminUploadDraft.session.token,
          { clips: uploadPayloads }
        )
        if (response.error) throw response.error
      } else {
        const requests = uploadPayloads.map((upload) => Data.updateClip(upload))
        const responses = await Promise.all(requests)
        const failedResponse = responses.find((response) => response.error)
        if (failedResponse) throw failedResponse.error
      }

      // To avoid having the next screen appearing too fast
      await new Promise((resolve) => setTimeout(resolve, 2000))

      // Go back to clips gallery
      setShowModal(false)

      return window.location.reload()
    } catch (err) {
      console.error(err)
      setIsLoading(false)
    }
  }

  const cancelUpload = async () => {
    try {
      if (adminUploadDraft) {
        await Data.abortUploadSession(adminUploadDraft.session.id, adminUploadDraft.session.token)
      }
    } catch (err) {
      console.error('Failed to abort administrator upload session', err)
    } finally {
      setShowModal(false)
    }
  }


  const renderProjectQuestion = (question: ProjectQuestionResponse, upload: IClipWithQuestions, index: number) => {
    const questionKey = toQuestionKeyFormat(question, index)

    switch (question.type) {
      case 'FreeText':
        return (
          <FreeTextQuestion
            key={questionKey}
            question={question}
            value={upload[questionKey]}
            onChange={handleChange}
            clipIndex={index}
            errorMessage={errorMessage}
            addValidationError={addValidationError}
            removeValidationError={removeValidationError}
          />
        )
      case 'SingleSelect':
        return (
          <SingleSelectQuestion
            key={questionKey}
            question={question}
            value={upload[questionKey]}
            onChange={handleChange}
            clipIndex={index}
            errorMessage={errorMessage}
          />
        )
      case 'MultiSelect':
        return (
          <MultiSelectQuestion
            key={questionKey}
            question={question}
            value={upload[questionKey]}
            onChange={handleChange}
            clipIndex={index}
            errorMessage={errorMessage}
          />
        )
      default:
        return null
    }
  }

  return (
    <Modal dimmer="blurring" open>
      <Modal.Header>Add clip information</Modal.Header>
      <Modal.Content>
        <Form error size="mini">
          {uploads.length > 0 &&
            uploads.map((upload, index) => (
              <Grid
                key={upload.id}
                stackable
                verticalAlign="middle"
                centered
                columns={2}
                divided
                className="clip-container"
              >
                <Grid.Column key={upload.id} textAlign="center" className="video-column">
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video tabIndex={-1} controls style={{ width: '100%', height: '231px' }}>
                    <source
                      src={(upload as IClipWithQuestions).previewUrl || `${S3_BUCKET_URL}/c/${upload.id}/0/0/1`}
                    />
                  </video>
                </Grid.Column>
                <Grid.Column className="form-column">
                  <div className="form-fields-container">
                    {/* Contributor name field */}
                    <Form.Field>
                      <CustomLabel>
                        <CustomLabel.Text text="Original content creator" />
                        <CustomLabel.WordCount
                          word={upload.contributor}
                          limit={maxLength.contributor}
                        ></CustomLabel.WordCount>
                      </CustomLabel>

                      <Form.Input
                        maxLength={maxLength.contributor}
                        name="contributor"
                        value={upload.contributor || ''}
                        onChange={(e, { name, value }) => handleChange(name, value, index)}
                        placeholder="i.e. Justin, Jeremy..."
                        error={
                          errorMessage &&
                          errorMessage.includes(`${index}-name`) && {
                            content: `Please enter the original content creator's name`,
                            pointing: 'above',
                          }
                        }
                      />
                    </Form.Field>

                    {displaySetting.showRating && (
                      <Form.Field>
                        <label>{displaySetting.rating || 'Leave a rating'} </label>
                        <Form.Input
                          error={
                            errorMessage &&
                            errorMessage.includes(`${index}-rating`) && {
                              content: `Please choose a rating from 1 to 5 stars`,
                              pointing: 'above',
                            }
                          }
                        >
                          <Rating
                            icon="star"
                            defaultRating={0}
                            maxRating={5}
                            size="massive"
                            name="rating"
                            onRate={(e, { name, rating: value }) => handleChange(name, value, index)}
                          />
                        </Form.Input>
                      </Form.Field>
                    )}

                    {/* Location Field */}
                    {displaySetting.showLocation && (
                      <UploadLocationField
                        index={index}
                        upload={upload}
                        placeSuggestions={placeSuggestionsArr[index]}
                        setLocation={setLocation}
                        resetLocation={resetLocation}
                      />
                    )}

                    {projectQuestions && projectQuestions.length > 0 && (
                      <div>
                        <Header as="h2">Qualifying Questions</Header>
                        {projectQuestions.map((question) => renderProjectQuestion(question, upload, index))}
                      </div>
                    )}
                  </div>
                </Grid.Column>
              </Grid>
            ))}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button fluid={isMobile} disabled={isLoading} onClick={cancelUpload}>
          Cancel upload
        </Button>
        <Button fluid={isMobile} primary loading={isLoading} disabled={!!errorMessage?.length} onClick={validateForm}>
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

interface ClipAnswer {
  questionId: number
  value?: string
  datasetRowNumbers?: number[]
}

// Extend the IClip interface to allow dynamic properties
interface IClipWithQuestions extends IClip {
  [key: string]: any
}

interface IProps {
  clipIds: number[]
  displaySetting: any
  uploads: IClip[]
  projectQuestions: ProjectQuestionResponse[] | null
  setUploads: (arg: IClip[]) => void
  setShowModal: (arg: boolean) => void
  adminUploadDraft?: AdminUploadDraft | null
}

export default UploadForm
